import React from 'react';
import { Circle, G, Line, Path, Polygon, Rect, Text as SvgText } from 'react-native-svg';
import { DiagramCanvas } from '../DiagramCanvas';
import { FormulaTag } from '../FormulaTag';
import { LeaderLine } from '../LeaderLine';
import { DT } from '../tokens';

/** Belt length BL — driver + driven sheaves with belt path, CD dimension, V-groove detail. */
export function DiagramFanBeltLength() {
  // Driver (left, smaller)
  const x1 = 95, y1 = 130, r1 = 28;
  // Driven (right, larger)
  const x2 = 260, y2 = 130, r2 = 48;

  return (
    <DiagramCanvas>
      {/* Belt — top tangent */}
      <Line x1={x1} y1={y1 - r1} x2={x2} y2={y2 - r2} stroke={DT.varColor} strokeWidth="3" />
      {/* Belt — bottom tangent */}
      <Line x1={x1} y1={y1 + r1} x2={x2} y2={y2 + r2} stroke={DT.varColor} strokeWidth="3" />
      {/* Wrap arcs (approximations) */}
      <Path d={`M${x1},${y1 - r1} A${r1},${r1} 0 0 0 ${x1},${y1 + r1}`}
        fill="none" stroke={DT.varColor} strokeWidth="3" />
      <Path d={`M${x2},${y2 - r2} A${r2},${r2} 0 0 1 ${x2},${y2 + r2}`}
        fill="none" stroke={DT.varColor} strokeWidth="3" />

      {/* Sheaves */}
      <Circle cx={x1} cy={y1} r={r1} fill={DT.fill} stroke={DT.stroke} strokeWidth="1.8" />
      <Circle cx={x1} cy={y1} r={r1 - 6} fill="none" stroke={DT.stroke} strokeWidth="0.8" opacity={0.6} />
      <Circle cx={x1} cy={y1} r={4} fill={DT.stroke} />
      <Line x1={x1 - r1 + 4} y1={y1} x2={x1 + r1 - 4} y2={y1} stroke={DT.dim} strokeWidth="0.6" strokeDasharray="2,2" />

      <Circle cx={x2} cy={y2} r={r2} fill={DT.fill} stroke={DT.stroke} strokeWidth="1.8" />
      <Circle cx={x2} cy={y2} r={r2 - 6} fill="none" stroke={DT.stroke} strokeWidth="0.8" opacity={0.6} />
      <Circle cx={x2} cy={y2} r={4} fill={DT.stroke} />
      <Line x1={x2 - r2 + 4} y1={y2} x2={x2 + r2 - 4} y2={y2} stroke={DT.dim} strokeWidth="0.6" strokeDasharray="2,2" />

      {/* Diameter dimensions */}
      <Polygon points={`${x1 - r1},${y1} ${x1 - r1 + 5},${y1 - 3} ${x1 - r1 + 5},${y1 + 3}`} fill={DT.dim} />
      <Polygon points={`${x1 + r1},${y1} ${x1 + r1 - 5},${y1 - 3} ${x1 + r1 - 5},${y1 + 3}`} fill={DT.dim} />
      <SvgText x={x1} y={y1 - 4} fill={DT.dim} fontSize="8" fontFamily={DT.mono} textAnchor="middle">PD₁</SvgText>

      <Polygon points={`${x2 - r2},${y2} ${x2 - r2 + 5},${y2 - 3} ${x2 - r2 + 5},${y2 + 3}`} fill={DT.dim} />
      <Polygon points={`${x2 + r2},${y2} ${x2 + r2 - 5},${y2 - 3} ${x2 + r2 - 5},${y2 + 3}`} fill={DT.dim} />
      <SvgText x={x2} y={y2 - 4} fill={DT.dim} fontSize="8" fontFamily={DT.mono} textAnchor="middle">PD₂</SvgText>

      {/* Center distance */}
      <Line x1={x1} y1={y1 + r2 + 28} x2={x2} y2={y2 + r2 + 28} stroke={DT.dim} strokeWidth="1" />
      <Line x1={x1} y1={y1 + r2 + 24} x2={x1} y2={y1 + r2 + 32} stroke={DT.dim} strokeWidth="1" />
      <Line x1={x2} y1={y2 + r2 + 24} x2={x2} y2={y2 + r2 + 32} stroke={DT.dim} strokeWidth="1" />
      <SvgText x={(x1 + x2) / 2} y={y1 + r2 + 42} fill={DT.dim} fontSize="9" fontFamily={DT.mono} textAnchor="middle">CD (in)</SvgText>

      {/* Center marks */}
      <Circle cx={x1} cy={y1} r={1.5} fill={DT.dim} />
      <Circle cx={x2} cy={y2} r={1.5} fill={DT.dim} />
      <Line x1={x1} y1={y1} x2={x1} y2={y1 + r2 + 26} stroke={DT.dim} strokeWidth="0.5" strokeDasharray="2,2" />
      <Line x1={x2} y1={y2} x2={x2} y2={y2 + r2 + 26} stroke={DT.dim} strokeWidth="0.5" strokeDasharray="2,2" />

      {/* Labels */}
      <LeaderLine x1={x1} y1={y1 - r1} x2={50} y2={50} varName="motor" desc="driver" anchor="start" />
      <LeaderLine x1={x2} y1={y2 - r2} x2={310} y2={50} varName="fan" desc="driven" anchor="end" />
      <LeaderLine x1={(x1 + x2) / 2} y1={y1 - r2 + 12} x2={(x1 + x2) / 2} y2={210} varName="BL" desc="belt length (in)" anchor="middle" />

      <FormulaTag formula="BL = 2·CD + 1.57(PD₁+PD₂) + (PD₂−PD₁)²/(4·CD)" />
    </DiagramCanvas>
  );
}
